import {skipToken} from '@reduxjs/toolkit/query';
import {
  Alert,
  Button,
  DataStatus,
  Dropdown,
  DropdownItem,
  Separator,
  useTranslationContext,
} from 'platform/components';
import {Box, Heading, Hide, HStack, Link, Show, Stack, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useState} from 'react';

import {concat, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isPositive} from 'ramda-adjunct';

import {useGetTenantQuery, useSourcingAllowedCountryAndCurrencyQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useCreateInterestPriceReportMutation,
  useCreateInterestPriceReportPreviewQuery,
  useGetInterestPriceReportsQuery,
} from '@dms/api/finance';
import {
  useCreatePriceReportRequestMutation,
  useCreateVehiclePriceReportMutation,
  useCreateVehiclePriceReportPreviewQuery,
  useGetVehiclePriceReportsQuery,
} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';

import {Nullish, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {DEFAULT_COUNTRY} from '../../constants/country';
import {useCountry} from '../../hooks/useCountry';
import {getNaturalPersonFullName} from '../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../utils/handleApiError';
import {PriceReportLine} from './components/PriceReportLine';
import {PriceReportWidgetLogo} from './components/PriceReportWidgetLogo';

export type PriceReportWidgetContext = 'vehicle' | 'interest';

interface PriceReportWidgetProps extends TestIdProps {
  resourceId: string | Nullish;
  vehicleType: string | Nullish;
  context: PriceReportWidgetContext;
}

export function PriceReportWidget(props: PriceReportWidgetProps) {
  const formatDateTime = useDateTimeFormatter();
  const t = useTranslationContext();
  const [error, setError] = useState<string | string[]>([]);
  const [createVehiclePriceReport, {isLoading: isCreateVehiclePriceReportLoading}] =
    useCreateVehiclePriceReportMutation();
  const [createPriceReportRequest, {isLoading: isCreatePriceReportRequestLoading}] =
    useCreatePriceReportRequestMutation();
  const [createInterestPriceReport, {isLoading: isCreateInterestPriceReportLoading}] =
    useCreateInterestPriceReportMutation();
  const {data} = useSourcingAllowedCountryAndCurrencyQuery();
  const {data: tenant} = useGetTenantQuery();
  const {getCountryName} = useCountry();
  const [country, setCountry] = useState<string>(tenant?.country ?? DEFAULT_COUNTRY);

  const isPriceReportAvailable =
    isNotNil(props.resourceId) &&
    ['VEHICLETYPE_PASSENGER_CAR', 'VEHICLETYPE_VAN'].includes(props.vehicleType ?? '');
  const isInterestContext = props.context === 'interest';

  const {data: vehiclePriceReports = [], isLoading: isVehiclePriceReportsLoading} =
    useGetVehiclePriceReportsQuery(
      isNotNil(props.resourceId) ? {vehicleId: props.resourceId} : skipToken,
      {skip: isInterestContext || !isPriceReportAvailable}
    );

  const {
    data: vehiclePriceReportPreview,
    isFetching: isVehiclePriceReportPreviewFetching,
    isError: isVehiclePriceReportPreviewError,
  } = useCreateVehiclePriceReportPreviewQuery(
    isNotNil(props.resourceId)
      ? {vehicleId: props.resourceId, createVehiclePriceReportRequestBody: {country}}
      : skipToken,
    {skip: isInterestContext || !isPriceReportAvailable}
  );
  const {
    data: interestPriceReportPreview,
    isFetching: isInterestPriceReportPreviewFetching,
    isError: isInterestPriceReportPreviewError,
  } = useCreateInterestPriceReportPreviewQuery(
    isNotNil(props.resourceId)
      ? {interestId: props.resourceId, createVehiclePriceReportRequestBody: {country}}
      : skipToken,
    {skip: !isInterestContext || !isPriceReportAvailable}
  );
  const soldVehiclesCount =
    vehiclePriceReportPreview?.sold ?? interestPriceReportPreview?.sold ?? 0;
  const stockVehiclesCount =
    vehiclePriceReportPreview?.stock ?? interestPriceReportPreview?.stock ?? 0;
  const similarVehiclesCount = soldVehiclesCount + stockVehiclesCount;

  const {data: interestPriceReports = [], isLoading: isInterestPriceReportsLoading} =
    useGetInterestPriceReportsQuery(
      {interestId: props.resourceId!},
      {skip: !isInterestContext || !isPriceReportAvailable}
    );

  const priceReports = concat(vehiclePriceReports, interestPriceReports);

  const isDataLoading = isVehiclePriceReportsLoading || isInterestPriceReportsLoading;
  const isCreateLoading =
    isCreateVehiclePriceReportLoading ||
    isCreatePriceReportRequestLoading ||
    isCreateInterestPriceReportLoading;

  const [newestReport, ...olderReports] = priceReports;

  const {data: user} = useGetUserQuery(
    {id: newestReport?.createdBy ?? ''},
    {skip: isNilOrEmpty(newestReport?.createdBy) || !isPriceReportAvailable}
  );

  if (!isPriceReportAvailable) {
    return null;
  }

  const userFullName = getNaturalPersonFullName({
    firstName: user?.firstName,
    lastName: user?.lastName,
  });

  const errors = Array.isArray(error) ? error : [error];

  const getCreateReportMutation = () => {
    const resourceId = props.resourceId;
    if (isNil(resourceId)) {
      throw new Error('Resource ID is required');
    }
    if (isInterestContext) {
      return createInterestPriceReport({interestId: resourceId, body: {country}}).unwrap();
    }
    if (similarVehiclesCount > 0) {
      return createVehiclePriceReport({vehicleId: resourceId, body: {country}}).unwrap();
    }
    return createPriceReportRequest({vehicleId: resourceId, body: {country}}).unwrap();
  };

  const onCreateReport = () =>
    getCreateReportMutation()
      .then((data) => window.open(data.url))
      .catch((err: Error) =>
        handleApiError(err, {
          silent: true,
          callback: ({error}) => setError(getApiErrorMessages(error)),
        })
      );

  return (
    <VStack spacing={2}>
      <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" overflow="hidden">
        <Stack direction={['column', 'row']}>
          <PriceReportWidgetLogo data-testid={props['data-testid']} />
          <Box padding={4} flex={1}>
            <VStack spacing={3}>
              <VStack spacing={2}>
                <HStack align="center" justify="space-between">
                  <Heading size={4}>{t('entity.priceReport.title')}</Heading>
                  <HStack align="center" spacing={2}>
                    <Text size="xSmall" color="secondary">
                      {t('entity.priceReport.market')}
                    </Text>
                    <Dropdown
                      data-testid={suffixTestId('countryDropdown', props)}
                      dropdownControl={
                        <Link
                          data-testid={suffixTestId('countryLink', props)}
                          size="small"
                          title={getCountryName(country)}
                          rightIcon="hardware/keyboard_arrow_down"
                        />
                      }
                    >
                      {data?.countries?.map((code, index) => (
                        <DropdownItem
                          key={code}
                          data-testid={suffixTestId(`country-item[${index}]`, props)}
                          label={getCountryName(code)}
                          onClick={() => setCountry(code)}
                        />
                      ))}
                    </Dropdown>
                  </HStack>
                </HStack>
                <Hide when={newestReport}>
                  <Text size="xSmall" color="secondary">
                    {t('entity.priceReport.description')}
                  </Text>
                </Hide>
              </VStack>
              <DataStatus isLoading={isDataLoading}>
                <VStack spacing={2}>
                  <Show when={newestReport}>
                    <HStack spacing={2}>
                      <Button
                        data-testid={suffixTestId('viewResults', props)}
                        variant="secondary"
                        rightIcon="action/launch"
                        onClick={() => window.open(newestReport!.url)}
                        title={t('entity.priceReport.viewResults')}
                      />
                      <Button
                        data-testid={suffixTestId('newReportButton', props)}
                        variant="secondary"
                        onClick={onCreateReport}
                        isLoading={isCreateLoading}
                        title={t('entity.priceReport.newReport')}
                      />
                    </HStack>
                    <HStack spacing={3}>
                      <Text
                        size="xSmall"
                        color="tertiary"
                        data-testid={suffixTestId('recentReport', props)}
                      >
                        {`${t('entity.priceReport.recentReport')} ${
                          newestReport?.createdAt
                            ? formatDateTime('dateShort', parseDate(newestReport.createdAt))
                            : ''
                        }`}
                        {isNotNilOrEmpty(userFullName) ? `, ${userFullName}` : ''}
                      </Text>
                      <Show when={isNotNil(newestReport?.country)}>
                        <Text
                          size="xSmall"
                          color="secondary"
                          data-testid={suffixTestId('recentReport-country', props)}
                        >
                          ({newestReport?.country})
                        </Text>
                      </Show>
                    </HStack>
                  </Show>
                  <Show when={isPositive(olderReports.length)}>
                    <Separator spacing={1} />
                    <VStack spacing={3}>
                      {olderReports.map((report, index) => (
                        <PriceReportLine
                          data-testid={suffixTestId(`report[${index}]`, props)}
                          key={report.id ?? report.requestId}
                          report={report}
                        />
                      ))}
                    </VStack>
                  </Show>
                  <Hide when={newestReport}>
                    <Box>
                      <Button
                        data-testid={suffixTestId('getReportButton', props)}
                        variant="secondary"
                        onClick={onCreateReport}
                        isLoading={isCreateLoading}
                        title={t('entity.priceReport.getReport')}
                      />
                    </Box>
                  </Hide>
                </VStack>
              </DataStatus>
            </VStack>
          </Box>
        </Stack>
      </Box>
      <Hide when={isVehiclePriceReportPreviewError || isInterestPriceReportPreviewError}>
        <DataStatus
          isLoading={isVehiclePriceReportPreviewFetching || isInterestPriceReportPreviewFetching}
          minHeight={9}
        >
          <Alert
            type="inline"
            variant={similarVehiclesCount === 0 ? 'warning' : 'info'}
            message={
              similarVehiclesCount === 0
                ? t('entity.priceReport.noSimilarVehicles')
                : t('entity.priceReport.preview', {
                    vehicleCount: similarVehiclesCount,
                  })
            }
          />
        </DataStatus>
      </Hide>
      {errors.map((error, index) => (
        <Alert
          data-testid={suffixTestId(`errorAlert[${index}]`, props)}
          key={error}
          type="inline"
          variant="warning"
          message={error}
        />
      ))}
    </VStack>
  );
}
